import React from 'react';
import { Box, Typography, List, Button, Divider } from '@material-ui/core';
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  AddBox as AddBoxIcon,
  ExitToApp as ExitToAppIcon,
  Assignment as AssignmentIcon,
  Cancel as CancelIcon,
  Launch as LaunchIcon
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import StepItem from './components/StepItem';
import { shouldUseCallOffInAutomationModals } from '../../../../utils/tenantFeatures';

const SuccessScreen = ({
  data,
  objects = { products: {}, varieties: {}, fruits: {} },
  onMainAction,
  onSecondaryAction,
}) => {
  const { t } = useTranslation();
  const useCallOffReference = shouldUseCallOffInAutomationModals();
  // 1. Extraemos la data del orquestador
  // newLots contiene: { id, quantity, productId, varietyId, processId }
  const {
    status,
    newLots = [],
    originLotId,
    closedProcessId,
    closedProcessTypeName,
    callOff,
    lotId,
  } = data;
  const firstProcessId = newLots[0]?.processId;
  const firstProcessTypeName = newLots[0]?.processTypeName;
  const hasSubdivision = newLots.length > 1;

  return (
    <Box width="100%" px={1}>
      <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
        <CheckCircleOutlineIcon style={{ fontSize: 50, color: '#00a680' }} />
        <Typography variant="h6" style={{ fontWeight: 'bold', marginTop: 8, color: '#004d40' }}>
          {status === 'completed_route'
            ? t('process.success.completedRoute')
            : t('process.success.operationSuccessful')}
        </Typography>
      </Box>

      <List>
        {/* HITO 1: Cierre del proceso donde estaba el bulto */}
        {closedProcessId ? (
          <StepItem
            icon={<CancelIcon style={{ color: '#f44336' }} />}
            title={t('process.success.currentProcessClosed')}
            subtitle={t('process.success.currentProcessClosedSubtitle', {
              processRef: closedProcessTypeName || `#${closedProcessId}`,
            })}
          />
        ) : null}

        {/* HITO 2: Iteración N veces (Subdivisión o Único) */}
        {newLots.map((lot, index) => {
          const product = objects?.products?.[lot.productId];
          const variety = objects?.varieties?.[lot.varietyId];
          const fruit = objects?.fruits?.[product?.fruitId];

          return (
            <React.Fragment key={lot.id}>
              {/* Bloque A: El nuevo bulto físico */}
              <StepItem
                icon={fruit?.icon || <AddBoxIcon style={{ color: '#1976d2' }} />}
                title={t('process.success.outputPakCreated', { lotId: lot.id })}
                subtitle={t('process.success.outputPakDetail', {
                  product: product?.name || t('process.success.productFallback'),
                  variety: variety?.name || '',
                  quantity: lot.quantity,
                })}
              />

              {/* Bloque B: El nuevo proceso asociado */}
              <StepItem
                icon={<ExitToAppIcon style={{ color: '#9c27b0' }} />}
                title={t('process.success.newProcessStarted', {
                  processRef: lot.processTypeName || `#${lot.processId}`,
                })}
                subtitle={t('process.success.newProcessSubtitle', { lotId: lot.id })}
              />

              {/* Separador entre grupos de PAKs si hay subdivisión */}
              {index < newLots.length - 1 && (
                <Divider style={{ margin: '8px 0', backgroundColor: 'transparent' }} />
              )}
            </React.Fragment>
          );
        })}

        {useCallOffReference && !hasSubdivision && callOff ? (
          <StepItem
            icon={<AssignmentIcon style={{ color: '#1565c0' }} />}
            title={t('process.success.callOffReferenceTitle')}
            subtitle={t('process.success.callOffReferenceSubtitle', {
              callOff,
            })}
          />
        ) : null}

        {/* HITO 3: Trazabilidad del origen */}
        <StepItem
          icon={<AssignmentIcon style={{ color: '#ff9800' }} />}
          title={t('process.success.traceabilityRegistered')}
          subtitle={t('process.success.originPak', {
            lotId: originLotId || lotId || '-',
          })}
        />
      </List>

      <Box mt={3} display="flex" flexDirection="column" gridGap={12}>
        {/* Botón de acción principal: Abre el primer proceso de la lista */}
        {firstProcessId ? (
          <Button
            fullWidth
            variant="contained"
            style={{ backgroundColor: '#00897b', color: 'white', borderRadius: '8px', textTransform: 'none' }}
            startIcon={<LaunchIcon />}
            onClick={() => onMainAction(firstProcessId)}
          >
            {newLots.length > 1
              ? t('process.success.openFirstProcess', {
                  processRef: firstProcessTypeName || `#${firstProcessId}`,
                })
              : t('process.success.openProcess', {
                  processRef: firstProcessTypeName || `#${firstProcessId}`,
                })}
          </Button>
        ) : null}

        <Button
          fullWidth
          variant="outlined"
          style={{ color: '#00897b', borderColor: '#00897b', borderRadius: '8px', textTransform: 'none' }}
          onClick={onSecondaryAction}
        >
          {t('process.success.finishAndBack')}
        </Button>
      </Box>
    </Box>
  );
};

export default SuccessScreen;
